import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, X, ShoppingBag, Tag, ArrowLeft } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/button";
import { api, formatPrice, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateCart, removeFromCart } = useCart();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [processing, setProcessing] = useState(false);

  const subtotal = cart.subtotal || 0;
  const discount = appliedCoupon ? subtotal * (appliedCoupon.discount_percent / 100) : 0;
  const total = Math.max(subtotal - discount, 0);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon });
      setAppliedCoupon(data);
      toast.success(`Coupon "${data.code}" applied`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const checkout = async () => {
    if (!user) {
      navigate("/login?next=/cart");
      return;
    }
    if (!cart.items?.length) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setProcessing(false);
    }
  };

  if (!cart.items?.length) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="w-20 h-20 rounded-full bg-purple-50 grid place-items-center mx-auto mb-5">
            <ShoppingBag className="w-9 h-9 text-[#A855F7]" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{t("common.empty_cart")}</h2>
          <p className="text-slate-500 mt-2">{t("common.continue_shopping")}</p>
          <Button onClick={() => navigate("/catalog")} className="btn-neon mt-6 rounded-full px-6" data-testid="empty-cart-shop-btn">{t("common.shop_now")}</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" data-testid="cart-page">
      <div className="gf-container py-10">
        <div className="flex items-center gap-2 mb-6">
          <Link to="/catalog" className="text-sm text-slate-600 hover:text-[#A855F7] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {t("common.continue_shopping")}
          </Link>
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-8">{t("cart.title")}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          <div className="space-y-3">
            <AnimatePresence>
              {cart.items.map((it) => (
                <motion.div
                  key={it.product.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-4"
                  data-testid={`cart-item-${it.product.id}`}
                >
                  <Link to={`/product/${it.product.slug}`} className="shrink-0">
                    <img src={it.product.cover_image} alt={it.product.title} className="w-20 h-20 rounded-xl object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${it.product.slug}`} className="font-display font-semibold text-slate-900 hover:text-[#A855F7] line-clamp-1">{it.product.title}</Link>
                    <div className="text-sm text-slate-500">{it.product.platform}</div>
                    <div className="text-sm font-semibold text-slate-900 mt-1 sm:hidden">{formatPrice(it.line_total, lang)}</div>
                  </div>
                  <div className="hidden sm:block min-w-[110px] text-center text-slate-700 font-medium">{formatPrice(it.unit_price, lang)}</div>
                  <div className="flex items-center gap-1 rounded-full border border-slate-200 px-1">
                    <button onClick={() => updateCart(it.product.id, it.quantity - 1)} className="w-7 h-7 grid place-items-center text-slate-700 hover:bg-slate-100 rounded-full" data-testid={`qty-minus-${it.product.id}`}>
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center font-medium text-sm">{it.quantity}</span>
                    <button onClick={() => updateCart(it.product.id, it.quantity + 1)} className="w-7 h-7 grid place-items-center text-slate-700 hover:bg-slate-100 rounded-full" data-testid={`qty-plus-${it.product.id}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="hidden sm:block min-w-[100px] text-right font-display font-bold text-slate-900">{formatPrice(it.line_total, lang)}</div>
                  <button onClick={() => removeFromCart(it.product.id)} className="w-8 h-8 grid place-items-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" data-testid={`remove-${it.product.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <aside className="rounded-2xl bg-white border border-slate-200 p-6 h-fit sticky top-24" data-testid="cart-summary">
            <h3 className="font-display text-lg font-bold text-slate-900 mb-4">{t("cart.summary")}</h3>
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder={t("common.promo_code")}
                  className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#A855F7]"
                  data-testid="coupon-input"
                />
              </div>
              <Button onClick={applyCoupon} variant="outline" size="sm" className="h-10" data-testid="apply-coupon">{t("common.apply")}</Button>
            </div>

            <div className="space-y-2 py-3 border-y border-slate-100 text-sm">
              <Row label={t("common.subtotal")} value={formatPrice(subtotal, lang)} />
              {discount > 0 && (
                <Row label={t("common.discount")} value={`- ${formatPrice(discount, lang)}`} valueClass="text-emerald-600" />
              )}
              <Row label={t("common.service_fee")} value={t("common.free")} />
            </div>
            <div className="flex items-center justify-between py-4">
              <span className="font-display font-bold text-slate-900">{t("common.total")}</span>
              <span className="font-display text-2xl font-bold text-slate-900" data-testid="cart-total">{formatPrice(total, lang)}</span>
            </div>
            <Button onClick={checkout} disabled={processing} className="btn-neon w-full h-12 rounded-xl font-semibold" data-testid="checkout-btn">
              {processing ? "…" : t("common.checkout")}
            </Button>
            <div className="text-xs text-slate-400 text-center mt-3">Secure payment via Stripe</div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-600">{label}</span>
      <span className={`font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}
